import { Box, Section } from '@factorialco/deprecated-design-system'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'
import legalEntities from 'modules/core/store/collections/legalEntities'

import taxonomies from 'modules/contracts/store/collections/taxonomies'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import locations from 'modules/locations/store/collections/locations'
import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { useConcepts } from 'modules/payroll_bundle/hooks/useConcepts'
import { useHasCompensations } from 'modules/payroll_bundle/hooks/useHasCompensations'
import { buildPayrollConceptsByLegalEntitySettings } from 'modules/payroll_bundle/lib/settingsPayrollConcept'
import CodesBasedOnSelectors from 'modules/payroll_integrations/components/Content/CodesBasedOnSelectors'
import Table from 'modules/payroll_integrations/components/Table'
import PayrollIntegration from 'modules/payroll_integrations/lib/catalog'
import type { Codeable, ColumnNames } from 'modules/payroll_integrations/lib/types'

import PayrollPaywallGuard from '../../PayrollPaywallGuard'
import EmployeeCodeConfiguration from './EmployeeCodeConfiguration'

const A3nomIntegration = () => {
  const INTEGRATION_NAME = PayrollIntegration.a3nom

  const { hasCompensations, loading, error } = useHasCompensations()

  const { concepts: payrollConcepts, loading: loadingConcepts } = useConcepts(
    undefined,
    [
      'deductions',
      'earnings_benefits_in_kind',
      'earnings_fixed_salary',
      'earnings_others',
      'earnings_variable',
    ],
    true,
    !hasCompensations
  )

  const { data: companyInstallationsData, loading: companyInstallationsDataLoading } =
    useGetMarketplaceCompanyInstallations(
      {
        integrationDatoIds: [integrationCodes.a3nom],
      },
      !hasCompensations
    )

  const a3nomInstallation = companyInstallationsData[0]

  const payrollConceptsByLegalEntity = buildPayrollConceptsByLegalEntitySettings(
    payrollConcepts,
    legalEntities
  )

  const codeables: Record<string, Codeable> = {
    company: {
      position: 1,
      collection: [companies.current],
      class: 'Company',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_paierh.settings.form.concept.company'
      ),
    },
    legalEntity: {
      position: 2,
      collection: legalEntities,
      class: 'LegalEntity',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_paierh.settings.form.concept.legal_entity'
      ),
    },
    location: {
      position: 4,
      collection: locations,
      class: 'Location',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3innuva.settings.form.concept.location'
      ),
    },
  }

  if (!hasCompensations) {
    codeables.taxonomy = {
      position: 3,
      collection: taxonomies.actives,
      class: 'Contracts::Compensations::Taxonomy',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.concept.taxonomy'
      ),
    }
  }

  if (hasCompensations) {
    codeables.payrollConcepts = {
      position: 3,
      collection: payrollConceptsByLegalEntity || [],
      class: 'Payroll::Concept',
      label: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.payroll_concept'
      ),
    }
  }

  const COLUMN_NAMES: ColumnNames = [
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.header.concept'
      ),
      colSpan: 4,
      attribute: 'concept',
    },
    {
      name: i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.header.value'
      ),
      colSpan: 4,
      attribute: 'value',
    },
  ]

  const { loading: loadingCollections } = useAsyncData(() => {
    return Promise.all([taxonomies.fetch()])
  }, [])

  const sectionDescription = hasCompensations
    ? i18n.t(
        'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.description_without_additional_compensations'
      )
    : i18n.t('app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.description')
  return (
    <PayrollPaywallGuard>
      <ResponsivePage.Body
        loading={
          loading || loadingCollections || loadingConcepts || companyInstallationsDataLoading
        }
        error={error}
      >
        <EmployeeCodeConfiguration />

        <CodesBasedOnSelectors integration_name={INTEGRATION_NAME} />

        <>
          <Section
            description={sectionDescription}
            illustration="bookmark"
            title={i18n.t(
              'app_store_v2.app_integrations.payroll_integrations_a3nom.settings.form.title'
            )}
          />
          <Box width="full" alignItems="center" gap="s32" paddingY="s48" paddingX="s64">
            <Table
              columnNames={COLUMN_NAMES}
              codeables={codeables}
              integration={INTEGRATION_NAME}
              integrationInstallation={a3nomInstallation}
            />
          </Box>
        </>
      </ResponsivePage.Body>
    </PayrollPaywallGuard>
  )
}

export default A3nomIntegration
